import IconChevronDown from '@apollo/icons/small/IconChevronDown.svg';
import {
  ButtonGroup,
  IconButton,
  Menu,
  MenuButton,
  MenuContent,
  MenuItem,
  MenuList,
} from '@apollo/orbit';
import { GraphQLInterfaceType, GraphQLObjectType } from 'graphql';
import React, { useMemo } from 'react';

import { Tooltip } from 'src/components/common/tooltip/Tooltip';

import { isMac } from '../helpers/isMac';

import { FieldToggle } from './fieldToggle/FieldToggle';
import { FieldToggleType } from './getFieldToggle/getFieldToggle';

export const SelectAllTypesButtonAndDropdown = ({
  fieldToggle,
  types,
}: {
  fieldToggle: FieldToggleType;
  types: Array<GraphQLObjectType | GraphQLInterfaceType>;
}) => {
  const areAllTypesSelected = useMemo(
    () => types.every((type) => fieldToggle?.areAllTypeFieldsSelected?.(type)),
    [fieldToggle, types],
  );
  const isAnyTypeSelected = fieldToggle?.isAnyTypeSelected();

  return (
    <ButtonGroup isAttached>
      <Tooltip
        placement="top"
        label={
          isAnyTypeSelected
            ? 'Remove All Type Fields'
            : `Click to add all type fields (${
                isMac ? '⌘' : 'Ctrl'
              } + Click to add all type fields recursively)`
        }
      >
        <IconButton
          aria-label={
            isAnyTypeSelected
              ? 'Remove All Type Fields'
              : `Click to add all type fields (${
                  isMac ? '⌘' : 'Ctrl'
                } + Click to add all type fields recursively)`
          }
          variant="hidden"
          size="sm"
          onClick={(event) => {
            if (isAnyTypeSelected) {
              fieldToggle?.deleteAllTypes(types);
            } else if (event.metaKey || event.ctrlKey) {
              types.forEach((type) =>
                fieldToggle?.insertTypeFieldsRecursively?.(type),
              );
            } else {
              types.forEach((type) => fieldToggle?.insertTypeFields(type));
            }
          }}
          icon={
            <FieldToggle
              checked={
                areAllTypesSelected
                  ? 'checked'
                  : isAnyTypeSelected
                    ? 'mixed'
                    : 'unchecked'
              }
            />
          }
        />
      </Tooltip>
      <Menu placement="bottom-start" autoSelect={false}>
        <MenuButton
          as={IconButton}
          variant="hidden"
          size="sm"
          aria-label="Select all types dropdown"
        >
          <IconChevronDown />
        </MenuButton>
        <MenuContent>
          <MenuList>
            <MenuItem
              onClick={() => {
                types.forEach((type) => fieldToggle?.insertTypeScalars?.(type));
              }}
            >
              Select all scalar fields
            </MenuItem>
            <MenuItem
              onClick={() => {
                types.forEach((type) =>
                  fieldToggle?.insertTypeFieldsRecursively?.(type),
                );
              }}
            >
              Select all fields recursively (up to 6 levels)
            </MenuItem>
          </MenuList>
        </MenuContent>
      </Menu>
    </ButtonGroup>
  );
};
